import { useQuery } from "@tanstack/react-query"
import { useRef, useState } from "react"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { EstadoError } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"

import { FichaObligacion } from "./FichaObligacion"
import {
  etiquetaPeriodicidad,
  type FilaTablero,
  fechaCorta,
  ORDEN_SEMAFORO,
  SEMAFORO,
  type Semaforo,
  type Tablero,
} from "./tipos"

/**
 * El tablero: cada obligación con su semáforo, lo urgente arriba.
 *
 * ⚠ **Las tarjetas filtran.** Una tarjeta que dice «3 vencidas» y no lleva a ellas
 * obliga a buscarlas en la tabla. Pulsar otra vez la misma quita el filtro.
 */
export function TableroCumplimiento() {
  const [filtro, setFiltro] = useState<Semaforo | null>(null)
  const [abierta, setAbierta] = useState<number | null>(null)
  const vista = useRef<HTMLDivElement | null>(null)
  const tablero = useQuery({
    queryKey: ["cumplimiento", "tablero"],
    queryFn: () => api.get<Tablero>("/api/v1/cumplimiento/tablero"),
  })

  if (tablero.isError) {
    return (
      <EstadoError
        titulo="No se pudo leer el tablero."
        error={tablero.error}
        onReintentar={() => tablero.refetch()}
      />
    )
  }
  const filas = [...(tablero.data?.filas ?? [])].sort(
    (a, b) =>
      ORDEN_SEMAFORO.indexOf(a.estado) - ORDEN_SEMAFORO.indexOf(b.estado) ||
      (a.vence ?? "9999").localeCompare(b.vence ?? "9999"),
  )
  const visibles = filtro ? filas.filter((f) => f.estado === filtro) : filas
  const fila: FilaTablero | undefined = filas.find((f) => f.obligacion.id === abierta)

  return (
    <div>
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
        {ORDEN_SEMAFORO.map((s) => (
          <button
            key={s}
            type="button"
            aria-pressed={filtro === s}
            className={`tarjeta tarjeta-interactiva foco-cristal p-3 text-left ${filtro === s ? "activo-cristal" : ""}`}
            onClick={() => setFiltro(filtro === s ? null : s)}
          >
            <span className="block text-2xl font-semibold tabular-nums">
              {tablero.data?.resumen[s] ?? "–"}
            </span>
            <Insignia tono={SEMAFORO[s].tono}>{SEMAFORO[s].etiqueta}</Insignia>
          </button>
        ))}
      </div>

      {visibles.length > 0 && (
        <div className="mb-3 flex justify-end">
          <Exportar
            titulo="Cumplimiento ante entes"
            subtitulo={filtro ? `Estado: ${SEMAFORO[filtro].etiqueta}` : "Todas las obligaciones"}
            contenido={vista}
          />
        </div>
      )}

      <div ref={vista}>
        <Tabla etiqueta="Obligaciones ante entes gubernamentales y parafiscales">
          <Cabecera>
            <Th>Ente</Th>
            <Th>Obligación</Th>
            <Th>Periodicidad</Th>
            <Th>Último registro</Th>
            <Th>Vence</Th>
            <Th>Estado</Th>
          </Cabecera>
          <Cuerpo>
            {tablero.isPending ? (
              <TablaVacia>Cargando…</TablaVacia>
            ) : visibles.length === 0 ? (
              <TablaVacia>
                {filtro ? "Ninguna en ese estado." : "No hay obligaciones activas."}
              </TablaVacia>
            ) : (
              visibles.map((f) => (
                <Fila
                  key={f.obligacion.id}
                  className="cursor-pointer"
                  onClick={() => setAbierta(f.obligacion.id)}
                >
                  <Td className="font-medium">{f.ente.codigo}</Td>
                  <Td data-exportar-valor={f.obligacion.nombre}>
                    <button
                      type="button"
                      className="foco-cristal text-left underline-offset-2 hover:underline"
                      onClick={(e) => {
                        e.stopPropagation()
                        setAbierta(f.obligacion.id)
                      }}
                    >
                      {f.obligacion.nombre}
                    </button>
                  </Td>
                  <Td>{etiquetaPeriodicidad(f.obligacion.periodicidad)}</Td>
                  <Td numero={true}>
                    {f.ultimo ? `${f.ultimo.periodo} · ${fechaCorta(f.ultimo.fecha)}` : "—"}
                  </Td>
                  <Td numero={true}>{fechaCorta(f.vence)}</Td>
                  <Td>
                    <Insignia tono={SEMAFORO[f.estado].tono}>
                      {SEMAFORO[f.estado].etiqueta}
                    </Insignia>
                  </Td>
                </Fila>
              ))
            )}
          </Cuerpo>
        </Tabla>
      </div>

      {fila && <FichaObligacion fila={fila} onCerrar={() => setAbierta(null)} />}
    </div>
  )
}
